<template>
  <section
    :class="[
      $style.card,
      compact && $style.compact,
      overviewLayout && $style.overviewLayout,
    ]"
    :aria-labelledby="headingId"
  >
    <div :class="$style.header">
      <div>
        <p :class="$style.eyebrow">{{ eyebrow }}</p>
        <h2
          :id="headingId"
          :class="$style.title"
        >
          {{ title }}
        </h2>
      </div>
    </div>

    <div
      v-if="overviewLayout"
      :class="$style.overviewMetrics"
    >
      <div :class="[$style.metricGroup, $style.investmentGroup]">
        <h3 :class="$style.groupTitle">Investment</h3>
        <div :class="$style.groupItems">
          <div :class="$style.groupItem">
            <span :class="$style.groupLabel">Hiring-team effort</span>
            <strong
              ><span :class="$style.upTo">up to</span>
              {{ roundImpactHoursV1(result.plannedHiringTeamHours) }}
              hrs</strong
            >
          </div>
          <div :class="$style.groupItem">
            <span :class="$style.groupLabel">Hiring-team investment</span>
            <strong
              ><span :class="$style.upTo">up to</span> ${{
                formatCost(result.plannedHiringTeamCost)
              }}</strong
            >
          </div>
        </div>
        <p :class="$style.investmentNote">
          Across interviewers, recruiters and hiring managers
        </p>
      </div>
      <div :class="[$style.metricGroup, $style.returnGroup]">
        <h3 :class="$style.groupTitle">Potential return</h3>
        <div :class="$style.groupItems">
          <div :class="$style.groupItem">
            <span :class="$style.groupLabel">Potential time savings</span>
            <strong
              ><span :class="$style.upTo">up to</span>
              {{ roundImpactHoursV1(result.estimatedTimeSavingsHours) }}
              hrs</strong
            >
          </div>
          <div :class="$style.groupItem">
            <span :class="$style.groupLabel">Potential cost savings</span>
            <strong
              ><span :class="$style.upTo">up to</span> ${{
                formatCost(result.estimatedCostSavings)
              }}</strong
            >
          </div>
        </div>
      </div>
      <div
        v-if="candidateSentiment"
        :class="[$style.metricGroup, $style.experienceGroup]"
      >
        <h3 :class="$style.groupTitle">Experience</h3>
        <div :class="$style.groupItem">
          <span :class="$style.groupLabel">Candidate sentiment</span>
          <div :class="$style.sentimentValueRow">
            <strong>{{ candidateSentiment.value }}</strong>
            <div
              :class="$style.sentimentScale"
              role="img"
              :aria-label="`Candidate sentiment: ${candidateSentiment.value}; industry average: ${candidateSentiment.industryAvg}`"
            >
              <div
                :class="$style.sentimentBars"
                aria-hidden="true"
              >
                <span
                  v-for="level in 3"
                  :key="level"
                  :class="{ [$style.barActive]: level <= sentimentRank }"
                />
              </div>
              <div
                :class="$style.sentimentLabels"
                aria-hidden="true"
              >
                <span>Low</span><span>▲ Avg</span><span>High</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      :class="[
        $style.metrics,
        candidateSentiment && $style.metricsWithSentiment,
      ]"
    >
      <div :class="$style.metric">
        <span :class="$style.metricLabel">Hiring-team effort</span>
        <strong
          >Up to
          {{ roundImpactHoursV1(result.plannedHiringTeamHours) }} hours</strong
        >
      </div>
      <div :class="$style.metric">
        <span :class="$style.metricLabel">Hiring-team investment</span>
        <strong>Up to ${{ formatCost(result.plannedHiringTeamCost) }}</strong>
      </div>
      <div :class="[$style.metric, $style.positive]">
        <span :class="$style.metricLabel">Potential time savings</span>
        <strong
          >Up to
          {{ roundImpactHoursV1(result.estimatedTimeSavingsHours) }}
          hours</strong
        >
      </div>
      <div :class="[$style.metric, $style.positive]">
        <span :class="$style.metricLabel">Potential cost savings</span>
        <strong>Up to ${{ formatCost(result.estimatedCostSavings) }}</strong>
      </div>
      <div
        v-if="candidateSentiment"
        :class="$style.metric"
      >
        <span :class="$style.metricLabel">Candidate sentiment</span>
        <strong>{{ candidateSentiment.value }}</strong>
        <span :class="$style.metricBenchmark">
          Industry avg: {{ candidateSentiment.industryAvg }}
        </span>
      </div>
    </div>

    <div :class="overviewLayout && $style.overviewFooter">
      <p :class="$style.estimateNote">
        Model-based potential benefit, not net savings. Actual results vary.
      </p>
      <details :class="$style.context">
        <summary>
          How this estimate works<ChevronDownIcon
            v-if="overviewLayout"
            :class="$style.chevronIcon"
          />
        </summary>
        <p>
          Based on your usual stage count and the model's assumptions, not an
          audited cost comparison or guaranteed savings. Time means hiring-team
          effort; cost values that time at ${{
            result.assumptions.loadedHourlyRate
          }}/hour.
        </p>
        <p>
          Potential savings combine estimated effort from fewer stages with
          {{
            result.assumptions.hiringKitPreparationSavingsHoursPerInterviewer *
            60
          }}
          minutes of preparation support per candidate per required interviewer
          when using the completed Hiring Kit. Adding interviewers can increase
          both planned effort and this potential benefit.
        </p>
        <p>
          The model assumes
          {{ result.assumptions.preparationHoursPerInterviewer * 60 }} minutes
          of preparation and
          {{ result.assumptions.evaluationHoursPerInterviewer * 60 }}
          minutes of evaluation per candidate per required interviewer, with
          {{ result.assumptions.candidateAdvancementRate * 100 }}% of candidates
          advancing per stage, subject to your hiring or finalist minimum.
        </p>
        <p>
          This is not net savings against your previous process. Added-stage
          investment is reported separately and is not deducted from the
          potential savings above. Actual results depend on how the plan is
          used.
        </p>
        <p v-if="result.additionalStageCount">
          This plan adds {{ result.additionalStageCount }} stage(s) beyond the
          usual count: approximately
          {{ roundImpactHoursV1(result.additionalStageInvestmentHours) }}
          hiring-team hours of additional investment.
        </p>
        <p v-else-if="result.stageReductionCount">
          This plan has {{ result.stageReductionCount }} fewer stage(s) than the
          usual process. Each reduced stage is modeled as a later-stage
          interview lasting
          {{ result.assumptions.representativeStageDurationHours }} hour(s) with
          {{ result.assumptions.representativeStageInterviewers }} interviewers.
        </p>
        <p
          v-if="candidateSentiment"
          :class="$style.learnMore"
        >
          Curious how these metrics are calculated?
          <a
            href="https://getyogen.com/resources/yogen-interview-stages-metrics-tutorial"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn More <ExternalLinkIcon :class="$style.externalIcon" />
          </a>
        </p>
      </details>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { useId } from 'vue'
  import type { MetricValue } from '~/constants/hiringKitTypes'
  import type { ImpactModelResultV1 } from '~/utils/impactModelV1'
  import { roundImpactCostV1, roundImpactHoursV1 } from '~/utils/impactModelV1'
  import ExternalLinkIcon from '~/assets/svg/externalLink.svg'
  import ChevronDownIcon from '~/assets/svg/chevronDown.svg'

  const headingId = useId()

  const props = withDefaults(
    defineProps<{
      result: ImpactModelResultV1
      candidateSentiment?: MetricValue
      compact?: boolean
      overviewLayout?: boolean
      eyebrow?: string
      title?: string
    }>(),
    {
      compact: false,
      overviewLayout: false,
      candidateSentiment: undefined,
      eyebrow: 'Live estimate',
      title: 'Estimated impact of this plan',
    },
  )

  function formatCost(value: number): string {
    return roundImpactCostV1(value).toLocaleString('en-US')
  }

  const sentimentRank = computed(() => {
    const value = String(props.candidateSentiment?.value ?? '').toLowerCase()
    return value === 'high' ? 3 : value === 'medium' ? 2 : 1
  })
</script>

<style module>
  .card {
    display: block;
    text-align: left;
    min-width: 0;
    padding: var(--space-20);
    margin: var(--space-20) 0;
    background: linear-gradient(
      135deg,
      var(--color-surface-accent) 0%,
      var(--color-surface-default) 70%
    );
    border: 1px solid var(--color-surface-accent);
    border-radius: var(--radius-md);
  }

  .overviewLayout {
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 0;
  }

  .overviewLayout .header {
    margin-bottom: var(--space-16);
  }

  .overviewMetrics {
    display: grid;
    grid-template-columns: 2.15fr 2.15fr 1.3fr;
    gap: var(--space-16);
  }

  .metricGroup {
    min-width: 0;
    padding: var(--space-16);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    box-shadow: var(--elevation-md);
  }

  .investmentGroup {
    background: var(--color-surface-default);
  }

  .returnGroup {
    background: var(--color-green-50);
  }

  .experienceGroup {
    background: var(--color-purple-50);
  }

  .groupTitle {
    margin: 0 0 var(--space-16);
    color: var(--color-text-secondary);
    font: var(--type-label-large-emphasized);
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  .returnGroup .groupTitle {
    color: var(--color-green-700);
  }

  .experienceGroup .groupTitle {
    color: var(--color-interactive-primary);
  }

  .groupItems {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .groupItems .groupItem:first-child {
    padding-right: var(--space-16);
  }

  .groupItems .groupItem + .groupItem {
    padding-left: var(--space-16);
    border-left: 1px solid var(--color-border-default);
  }

  .investmentNote {
    margin: var(--space-16) 0 0;
    padding-top: var(--space-12);
    border-top: 1px solid var(--color-border-default);
    color: var(--color-text-secondary);
    font: var(--type-body-medium);
  }

  .groupItem {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-8);
  }

  .groupLabel {
    color: var(--color-text-secondary);
    font: var(--type-body-medium);
  }

  .groupItem strong {
    color: var(--color-text-primary);
    font: var(--type-headline-small);
    white-space: nowrap;
  }

  .returnGroup .groupItem strong {
    color: var(--color-green-700);
  }

  .upTo {
    font: var(--type-body-medium-emphasized);
  }

  .sentimentValueRow {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-8);
  }

  .sentimentScale {
    width: 100%;
  }

  .sentimentBars {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-4);
  }

  .sentimentBars span {
    height: 4px;
    border-radius: var(--radius-full);
    background: var(--color-border-default);
  }

  .sentimentBars .barActive {
    background: var(--color-green-500);
  }

  .sentimentLabels {
    display: flex;
    justify-content: space-between;
    margin-top: var(--space-4);
    color: var(--color-text-secondary);
    font: var(--type-body-small);
  }

  .overviewFooter {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: var(--space-16);
    margin-top: var(--space-24);
    padding-bottom: var(--space-16);
    border-bottom: 1px solid var(--color-border-default);
  }

  .overviewFooter .estimateNote,
  .overviewFooter .context {
    margin: 0;
    font: var(--type-body-medium);
  }

  .overviewFooter .estimateNote {
    display: flex;
    align-items: center;
    gap: var(--space-8);
  }

  .overviewFooter .context summary {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    color: var(--color-interactive-primary);
    cursor: pointer;
    font: var(--type-label-large-emphasized);
    list-style: none;
  }

  .overviewFooter .context summary::-webkit-details-marker {
    display: none;
  }

  .chevronIcon {
    width: 18px;
    height: 18px;
    transition: transform 0.2s ease;
  }

  .overviewFooter .context[open] {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .overviewFooter .context[open] summary {
    position: absolute;
    top: var(--space-16);
    right: 0;
  }

  .overviewFooter .context[open] .chevronIcon {
    transform: rotate(180deg);
  }

  .estimateNote {
    margin: var(--space-8) 0 0;
    color: var(--color-text-secondary);
    font: var(--type-body-small);
  }

  .compact {
    flex-shrink: 0;
    margin: 0;
    padding: var(--space-12);
  }

  .compact .header {
    margin-bottom: var(--space-8);
  }

  .compact .title,
  .compact .metric strong {
    font: var(--type-body-medium-emphasized);
  }

  .compact .metric {
    padding: var(--space-8);
    gap: var(--space-4);
  }

  .compact .context {
    margin-top: var(--space-8);
    max-height: 20vh;
    overflow-y: auto;
  }

  .compact .metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 700px) {
    .compact .metrics {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .header {
    text-align: left;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-16);
    margin-bottom: var(--space-16);
  }

  .eyebrow {
    margin: 0 0 var(--space-4);
    color: var(--color-interactive-primary);
    font: var(--type-label-small-emphasized);
    letter-spacing: var(--type-label-small-emphasized-tracking);
    text-transform: uppercase;
  }

  .title {
    margin: 0;
    color: var(--color-text-primary);
    font: var(--type-title-large);
    letter-spacing: var(--type-title-large-tracking);
  }

  .metrics {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-12);
  }

  .metricsWithSentiment {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .metric {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
    padding: var(--space-12);
    background: var(--color-surface-default);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
  }

  .metric strong {
    font: var(--type-title-large);
    letter-spacing: var(--type-title-large-tracking);
  }

  .positive strong {
    color: var(--color-status-success);
  }

  .metricLabel {
    color: var(--color-text-secondary);
    font: var(--type-body-small);
    letter-spacing: var(--type-body-small-tracking);
  }

  .metricBenchmark {
    color: var(--color-text-disabled);
    font: var(--type-body-small);
  }

  .learnMore {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-8);
  }

  .learnMore a {
    display: inline-flex;
    align-items: center;
    gap: var(--space-4);
    color: var(--color-interactive-link);
    font: var(--type-label-large);
    text-decoration: none;
  }

  .learnMore a:hover {
    text-decoration: underline;
  }

  .externalIcon {
    width: 14px;
    height: 14px;
  }

  .context {
    margin: var(--space-16) 0 0;
    color: var(--color-text-secondary);
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
  }

  @media (max-width: 900px) {
    .overviewMetrics {
      grid-template-columns: 1fr;
    }

    .overviewFooter {
      grid-template-columns: 1fr;
    }

    .overviewFooter .context[open] {
      grid-column: 1;
      grid-row: auto;
    }

    .overviewFooter .context[open] summary {
      position: static;
    }

    .metrics,
    .metricsWithSentiment {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 520px) {
    .overviewLayout .header {
      flex-direction: column;
      gap: var(--space-12);
    }

    .groupItems {
      grid-template-columns: 1fr;
    }

    .groupItems .groupItem:first-child {
      padding-right: 0;
      padding-bottom: var(--space-16);
    }

    .groupItems .groupItem + .groupItem {
      padding-top: var(--space-16);
      padding-left: 0;
      border-top: 1px solid var(--color-border-default);
      border-left: 0;
    }

    .metrics,
    .metricsWithSentiment {
      grid-template-columns: 1fr;
    }
  }
</style>
